<template>
<div>
  <v-container fluid class="my-5">
    <v-layout row justify-center>
      <div style="width: 95%;">
          {{ $t('views.adminAdHocModels.title') }}
      </div>
      <!-- Searches, sorts and pages the rows itself (server-items-length), so that a page holds whole
           version families: itemsPerPage families per page, plus their expanded older versions -->
      <v-data-table
        dense
        :headers="headers"
        :items="pageItems"
        :server-items-length="familyCount"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :page.sync="page"
        :items-per-page.sync="itemsPerPage"
        :no-data-text="search ? '$vuetify.dataIterator.noResultsText' : '$vuetify.noDataText'"
        :item-class="itemClass"
        style="width: 95%;"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <!-- Shared dialogs must be outside item templates, to avoid declaring dialog code for each one of them -->
            <v-dialog v-model="createDialog" fullscreen hide-overlay transition="dialog-bottom-transition" persistent :retain-focus="false">
              <template v-slot:activator="{ on: dialog }">
                <v-btn small color="#212121" class="white--text" v-on="{...dialog}">
                  <v-icon class="align-center" color="primary">mdi-plus-thick</v-icon>
                  <span class="pl-2">{{ $t('views.adminAdHocModels.createButton') }}</span>
                </v-btn>
              </template>
              <create-ad-hoc-model v-if="createDialog" @close-dialog="createDialog = false; editItem = null;" @version-link-failed="versionLinkFailed" :created-by="authUser.uid" :existing-models="existingModels" :models="mmmModels" :editing="editItem" />
            </v-dialog>
            <v-dialog v-model="deployDialog" max-width="600" persistent :retain-focus="false">
              <v-card v-if="!deploying && authUser.exportTargets">
                <v-card-title class="headline">{{ $t('views.adminAdHocModels.deployDialog.title') }}</v-card-title>
                <v-card-text>
                  <v-text-field :label="$t('views.adminAdHocModels.deployDialog.sourceModelName')" v-model="deploySourceName" disabled></v-text-field>
                  <v-text-field :label="$t('views.adminAdHocModels.deployDialog.targetModelName')" v-model="deployTargetName"></v-text-field>
                  {{ $t('views.adminAdHocModels.deployDialog.targetEnvironment') }}
                  <v-select :items="authUser.exportTargets.map((item) => ({ text: item.name, value: item.id }))" v-model="deployTargetEnv"></v-select>
                  <v-text-field :label="$t('views.adminAdHocModels.deployDialog.confirmLabel')" v-model="deployConfirmEnv"></v-text-field>
                  <v-alert type="error" v-if="deployError">{{ deployError }}</v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="toggleDeployDialog(null)">{{ $t('views.adminAdHocModels.deployDialog.cancel') }}</v-btn>
                  <v-btn color="red darken-1" text @click="triggerDeploy()" :disabled="!deployTargetEnv || (deployConfirmEnv !== 'deploy')">{{ $t('views.adminAdHocModels.deployDialog.deploy') }}</v-btn>
                </v-card-actions>
              </v-card>
              <v-card v-else>
                <v-card-title class="headline">{{ $t('views.adminAdHocModels.deployDialog.title') }}</v-card-title>
                <v-card-text>
                  {{ $t('views.adminAdHocModels.deployDialog.deploying') }} ({{ deployElapsedTime }}s).
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text disabled>{{ $t('views.adminAdHocModels.deployDialog.cancel') }}</v-btn>
                  <v-btn color="red darken-1" text disabled>{{ $t('views.adminAdHocModels.deployDialog.deploy') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="versionDialog" max-width="600" persistent :retain-focus="false">
              <v-card v-if="versionItem">
                <v-card-title class="headline">{{ $t('views.adminAdHocModels.versionDialog.title') }}</v-card-title>
                <v-card-subtitle>{{ versionItem.name }}</v-card-subtitle>
                <v-card-text>
                  <model-version-fields v-model="versionForm" :models="mmmModels" :model="versionItem" />
                  <v-alert type="error" dense class="mt-4" v-if="versionError">{{ versionError }}</v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text :disabled="versionSaving" @click="closeVersionDialog">{{ $t('views.adminAdHocModels.versionDialog.cancel') }}</v-btn>
                  <v-btn color="primary" text :loading="versionSaving" :disabled="!versionFormChanged" @click="triggerSetVersion">{{ $t('views.adminAdHocModels.versionDialog.save') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="deleteDialog" max-width="400" :retain-focus="false">
              <v-card>
                <v-card-title class="headline">{{ $t('views.adminAdHocModels.deleteDialog.title') }}</v-card-title>
                <v-card-text>
                  {{ $t('views.adminAdHocModels.deleteDialog.deletingAdHocModel') }} <b>{{ deleteItem.id }}</b>
                  <br/>
                  {{ $t('views.adminAdHocModels.deleteDialog.confirmMessage') }}
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="deleteDialog = false">{{ $t('views.adminAdHocModels.deleteDialog.cancel') }}</v-btn>
                  <v-btn color="red darken-1" text @click="triggerDelete(deleteItem.id); deleteDialog = false">{{ $t('views.adminAdHocModels.deleteDialog.delete') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('views.adminAdHocModels.searchLabel')"
              single-line
              hide-details
            ></v-text-field>
          </v-toolbar>
        </template>
        <template v-slot:item.date_created="{ item }">
          {{ dateModified(item) }}
        </template>
        <template v-slot:item.name="{ item }">
          <v-btn v-if="!item.isOlderVersion && familySize(item) > 1" icon x-small class="mr-1" @click.stop="toggleFamily(item)">
            <v-icon small>{{ isFamilyExpanded(item) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
          </v-btn>
          <!-- Same size as the chevron above, so older versions' names line up with their current version's -->
          <v-btn v-else-if="item.isOlderVersion" icon x-small class="mr-1 version-row-spacer" tabindex="-1" aria-hidden="true">
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
          {{ item.name }}
          <v-tooltip bottom v-if="familySize(item) > 1">
            <template v-slot:activator="{ on }">
              <v-chip x-small class="ml-1" :color="item.isOlderVersion ? '' : 'primary'" v-on="on">
                v{{ versionNumber(item) }}<span v-if="!item.isOlderVersion">&nbsp;· {{ $t('views.adminAdHocModels.current') }}</span>
              </v-chip>
            </template>
            <div>{{ $t('views.adminAdHocModels.versionTooltip', { name: familyRootName(item) }) }}</div>
            <div>{{ $t('views.adminAdHocModels.versionDeployed', { date: versionDate(item) }) }}</div>
            <div v-if="item.version_notes">{{ item.version_notes }}</div>
          </v-tooltip>
        </template>
        <template v-slot:item.model_status="{ item }">
          <model-status :model="item"/>
        </template>
        <template v-slot:item.action="{ item }">
           <v-tooltip top v-if="(authUser.isAdmin || authUser.isScientist)">
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click="triggerRecalculateModelReport(item)">
                <v-icon v-if="hasModelReport(item)" small>mdi-file-chart</v-icon>
                <v-icon v-else small>mdi-timer-sand</v-icon>
              </v-btn>
            </template>
            {{ $t('views.adminAdHocModels.tooltips.recalculateModelReport') }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click.stop="createDialog = true; editItem = item">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            {{ $t('views.adminAdHocModels.tooltips.editAdHocModel') }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click.stop="openVersionDialog(item)">
                <v-icon small>mdi-source-branch</v-icon>
              </v-btn>
            </template>
            {{ $t('views.adminAdHocModels.tooltips.version') }}
          </v-tooltip>
          <v-tooltip top v-if="authUser.exportTargets && authUser.exportTargets.length > 0">
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click.stop="toggleDeployDialog(item)" :disabled="item.model_status !== 'Ready'">
                <v-icon small>mdi-rocket-launch</v-icon>
              </v-btn>
            </template>
            {{ $t('views.adminAdHocModels.tooltips.deploy') }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click.stop="deleteDialog = true; deleteItem = item">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            {{ $t('views.adminAdHocModels.tooltips.deleteAdHocModel') }}
          </v-tooltip>
        </template>
      </v-data-table>
      <v-snackbar v-model="snackbar" timeout="-1">
        {{ snackbarMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="blue" text v-bind="attrs" @click="snackbar = false; snackbarMessage = ''">
            {{ $t('views.adminAdHocModels.ok') }}
          </v-btn>
        </template>
      </v-snackbar>
    </v-layout>
  </v-container>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CreateAdHocModel from '@/components/CreateAdHocModel.vue'
import ModelStatus from '@/components/ModelStatus.vue'
import ModelVersionFields from '@/components/ModelVersionFields.vue'
import {
  countFamilyRows, familyRowsPage, filterFamilyRows, groupByFamily, lastFamilyPage, latestInFamily, sortFamilyRows,
  versionFamilyId, versionNumber
} from '@/utils/modelVersions.js'
import { hasModelReport } from '@/utils/modelReport.js'
import moment from 'moment'
import _ from 'lodash'

export default {
  data () {
    return {
      createDialog: false,
      deleteDialog: false,
      versionDialog: false,
      versionItem: null,
      versionForm: { familyId: null, notes: '' },
      versionSaving: false,
      versionError: null,
      deployDialog: false,
      deleteItem: '',
      editItem: null,
      search: '',
      // Version families expanded in the table, and the table's sort and page
      expandedFamilies: [],
      sortBy: [],
      sortDesc: [],
      page: 1,
      itemsPerPage: 10,
      deployTargetName: '',
      deploySourceCodename: '',
      deploySourceName: '',
      deployTargetEnv: null,
      deployError: null,
      deploying: false,
      deployElapsedTime: null,
      deployStartTime: null,
      deployConfirmEnv: '',
      snackbar: false,
      snackbarMessage: ''
    }
  },
  methods: {
    ...mapActions('admin', [
      'loadMmmModels',
      'deleteMmmModel',
      'recalculateModelReport',
      'deployModel',
      'updateModelStatus',
      'setModelVersion'
    ]),
    hasModelReport,
    versionNumber,
    familySize (item) {
      const versions = this.versionFamilies.get(versionFamilyId(item))
      return versions ? versions.length : 1
    },
    isLatestVersion (item) {
      const versions = this.versionFamilies.get(versionFamilyId(item))
      return !versions || latestInFamily(versions).id === item.id
    },
    familyRootName (item) {
      const versions = this.versionFamilies.get(versionFamilyId(item))
      return versions ? versions[0].name : item.name
    },
    // date_created: in production importModel creates the doc on deploy, so it is the deploy date.
    versionDate (item) {
      return item.date_created ? moment(item.date_created.toDate()).format('YYYY/MM/DD HH:mm') : ''
    },
    toggleFamily (item) {
      const familyId = versionFamilyId(item)
      this.expandedFamilies = this.expandedFamilies.includes(familyId)
        ? this.expandedFamilies.filter(id => id !== familyId)
        : this.expandedFamilies.concat(familyId)
    },
    isFamilyExpanded (item) {
      return this.expandedFamilies.includes(versionFamilyId(item))
    },
    itemClass (item) {
      return item.isOlderVersion ? 'version-row' : ''
    },
    // Vuetify's default table search: some column's value contains the search term
    matchesSearch (item, search) {
      return this.headers.some(header => {
        const value = _.get(item, header.value)
        return value != null && typeof value !== 'boolean' &&
          value.toString().toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
      })
    },
    openVersionDialog (item) {
      this.versionItem = item
      this.versionForm = {
        familyId: item.version_family || null,
        notes: item.version_notes || ''
      }
      this.versionError = null
      this.versionDialog = true
    },
    closeVersionDialog () {
      this.versionDialog = false
      this.versionItem = null
      this.versionError = null
    },
    triggerSetVersion () {
      this.versionSaving = true
      this.versionError = null
      this.setModelVersion({
        modelId: this.versionItem.id,
        familyId: this.versionForm.familyId,
        notes: this.versionForm.notes
      }).then(result => {
        this.versionSaving = false
        if (result.action === 'link') {
          this.snackbarMessage = this.$t('views.adminAdHocModels.versionDialog.linked', {
            name: this.versionItem.name,
            version: result.versionNumber,
            grants: result.grantsCopied,
            groups: result.groupsUpdated
          })
          this.snackbar = true
        }
        this.closeVersionDialog()
      }).catch(error => {
        this.versionSaving = false
        this.versionError = this.versionErrorMessage(error)
      })
    },
    versionErrorMessage (error) {
      // Error codes thrown by modelVersions-setModelVersion
      const keys = {
        'not-latest-version': 'notLatestVersion',
        'already-in-family': 'alreadyInFamily',
        'is-family-root': 'isFamilyRoot',
        'family-not-found': 'familyNotFound',
        'model-not-found': 'modelNotFound',
        'not-ad-hoc': 'notAdHoc'
      }
      const key = (error && keys[error.message]) || 'internal'
      return this.$t(`views.adminAdHocModels.versionErrors.${key}`)
    },
    versionLinkFailed ({ name, error }) {
      this.snackbarMessage = this.$t('views.adminAdHocModels.versionErrors.linkOnCreate', {
        name,
        reason: this.versionErrorMessage(error)
      })
      this.snackbar = true
    },
    toggleDeployDialog (item) {
      if (item) {
        this.deployDialog = true
        this.deploySourceCodename = item.id
        this.deploySourceName = item.name
        this.deployTargetName = item.name
        this.deployTargetEnv = null
        this.deployError = null
        this.deployConfirmEnv = ''
      } else {
        this.deployDialog = false
        this.deploySourceCodename = ''
        this.deploySourceName = ''
        this.deploytargetName = ''
        this.deployTargetEnv = null
        this.deployError = null
        this.deployConfirmEnv = ''
      }
    },
    triggerDeploy () {
      this.deployStartTime = performance.now()
      this.deployElapsedTime = 0
      this.deploying = true
      setInterval(() => {
        this.deployElapsedTime = Math.floor((performance.now() - this.deployStartTime) / 1000)
      }, 1000)
      this.deployModel({
        targetEnv: this.deployTargetEnv,
        sourceCodename: this.deploySourceCodename,
        targetName: this.deployTargetName,
        type: 'mmm'
      }).then(result => {
        if (result && result.data === 'model_name_exists') {
          console.log('Model name already exists in target environment')
          this.deployError = this.$t('views.adminAdHocModels.deployDialog.errorModelNameExists')
          this.deploying = false
        } else if (result && result.data === 'model_created') {
          console.log('Model created')
          this.updateModelStatus({
            modelCollectionPath: 'clients',
            modelId: this.deploySourceCodename,
            newStatus: 'Deployed'
          }).then(() => {
            this.deploying = false
            this.snackbarMessage = this.$t('views.adminAdHocModels.deployDialog.modelSuccessfullyDeployed')
            this.snackbar = true
            this.toggleDeployDialog(null)
          })
        } else {
          // Internal error
          console.error('Internal error #1', result)
          this.deployError = this.$t('views.adminAdHocModels.deployDialog.internalError')
          this.deploying = false
        }
      }).catch(error => {
        this.deploying = false
        console.error('Internal error #2', error)
        this.deployError = this.$t('views.adminAdHocModels.deployDialog.internalError')
      })
    },
    triggerRecalculateModelReport (item) {
      // Show immediate feedback
      this.snackbarMessage = this.$t('views.adminAdHocModels.snackbar.recalculating', { name: item.name })
      this.snackbar = true

      // Pass clientId and userId to the action
      this.recalculateModelReport({
        clientId: item.id,
        userId: this.authUser.uid
      }).then(result => {
        console.log('Calculating new model report')
        console.log(result)
        // Update snackbar with success message
        this.snackbarMessage = this.$t('views.adminAdHocModels.snackbar.recalculationStarted', { name: item.name })
        this.snackbar = true
      }).catch(error => {
        console.log(error)
        // Show error message
        this.snackbarMessage = this.$t('views.adminAdHocModels.snackbar.recalculationError', { name: item.name })
        this.snackbar = true
      })
    },
    triggerDelete (id) {
      this.deleteMmmModel({
        codename: id
      }).catch(error => {
        console.error(error)
        this.snackbarMessage = this.$t('views.adminAdHocModels.snackbar.deleteError', { name: id })
        this.snackbar = true
      })
    },
    dateModified (item) {
      const dateModified = item.date_edited ? item.date_edited : item.date_created
      return dateModified.toDate().toString().slice(4, 24)
    },
    customSort: function (items, sortBy, sortDesc, locale, customSorters) {
      const sortCol = sortBy[0]
      if (sortCol === 'date_created') {
        if (sortDesc[0]) {
          items.sort((a, b) => {
            let aDate = a.date_created
            let bDate = b.date_created
            if ('date_edited' in a) {
              aDate = a.date_edited
            }
            if ('date_edited' in b) {
              bDate = b.date_edited
            }
            return aDate - bDate
          })
        } else {
          items.sort((a, b) => {
            let aDate = a.date_created
            let bDate = b.date_created
            if ('date_edited' in a) {
              aDate = a.date_edited
            }
            if ('date_edited' in b) {
              bDate = b.date_edited
            }
            return bDate - aDate
          })
        }
      } else {
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return _.get(b, sortCol, '').toString().localeCompare(_.get(a, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        } else {
          items.sort((a, b) => {
            return _.get(a, sortCol, '').toString().localeCompare(_.get(b, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        }
      }
      return items
    }
  },
  computed: {
    ...mapGetters('admin', [
      'mmmModels'
    ]),
    ...mapGetters('auth', [
      'authUser'
    ]),
    versionFamilies () {
      return groupByFamily(this.mmmModels)
    },
    // One row per version family, its latest version; older versions follow it, as rows of their own, when it
    // is expanded. Searched and sorted.
    tableItems () {
      const rows = []
      this.mmmModels.filter(item => this.isLatestVersion(item)).forEach(item => {
        rows.push(item)
        if (this.isFamilyExpanded(item)) {
          this.versionFamilies.get(versionFamilyId(item)).slice(0, -1).reverse().forEach(version => {
            rows.push({ ...version, isOlderVersion: true })
          })
        }
      })
      const search = this.search ? this.search.trim() : ''
      const items = search ? filterFamilyRows(rows, item => this.matchesSearch(item, search)) : rows
      return sortFamilyRows(items, current => this.customSort(current, this.sortBy, this.sortDesc))
    },
    familyCount () {
      return countFamilyRows(this.tableItems)
    },
    pageItems () {
      return familyRowsPage(this.tableItems, this.page, this.itemsPerPage)
    },
    lastPage () {
      return lastFamilyPage(this.familyCount, this.itemsPerPage)
    },
    versionFormChanged () {
      if (!this.versionItem) {
        return false
      }
      return (this.versionForm.familyId || null) !== (this.versionItem.version_family || null) ||
        (this.versionForm.familyId && (this.versionForm.notes || '') !== (this.versionItem.version_notes || ''))
    },
    existingModels: function () {
      var models = []
      this.mmmModels.forEach(element => {
        models.push(element.id)
      })
      return models
    },
    headers: function () {
      let headers = [
        { text: this.$t('views.adminAdHocModels.headers.modelName'), value: 'name' },
        { text: this.$t('views.adminAdHocModels.headers.codename'), value: 'code' },
        { text: this.$t('views.adminAdHocModels.headers.pkl'), value: 'reference_model.pkl.name' },
        { text: this.$t('views.adminAdHocModels.headers.pklSize'), value: 'reference_model.pkl.size' },
        { text: this.$t('views.adminAdHocModels.headers.modified'), value: 'date_created' },
        { text: this.$t('views.adminAdHocModels.headers.actions'), value: 'action', sortable: false, width: '180px' }
      ]
      // Duplicated in Home.vue and App.vue
      // TODO: Unify

      // Status column only hidden in production for the peace of mind of admins
      if (window.location.hostname !== 'predixon.noisegrasp.com' && window.location.hostname !== 'app.noisegrasp.com' && window.location.hostname !== 'outflink.noisegrasp.com' && window.location.hostname !== 'deloitte.noisegrasp.com' && window.location.hostname !== 'nisum.noisegrasp.com' && window.location.hostname !== 'mckinsey.noisegrasp.com' && window.location.hostname !== 'comscore.noisegrasp.com') {
        headers.splice(headers.length - 1, 0, { text: this.$t('views.adminAdHocModels.headers.status'), value: 'model_status', width: '1px' })
      }
      return headers
    }
  },
  watch: {
    // Vuetify only keeps the page in range when it pages the rows itself
    lastPage (lastPage) {
      if (this.page > lastPage) {
        this.page = lastPage
      }
    }
  },
  components: {
    CreateAdHocModel,
    ModelStatus,
    ModelVersionFields
  },
  created: function () {
    this.loadMmmModels()
  }
}
</script>
<style scoped>
  /* Older versions of an expanded family */
  ::v-deep .version-row {
    background-color: #f5f5f5;
  }
  .version-row-spacer {
    visibility: hidden;
  }
</style>
